import clsx from 'clsx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import kebabCase from 'lodash/kebabCase';
import React, { useLayoutEffect, useState } from 'react';

import styles from './styles.module.scss';

const SHAREABLE_SCREENSHOT_ID = 'shareable-screenshot';
enum Theme {
  Dark = 'dark',
  Light = 'light',
}

type RenderScreenshotContent = false | Theme;

export const useForumPostScreenshot = (title: string) => {
  const [renderScreenshotContent, setRenderScreenshotContent] =
    useState<RenderScreenshotContent>(false);

  // Clicking download renders a hidden ForumPostScreenshot component
  // and downloads its contents as a screenshot with html2canvas
  const onDownloadScreenshot = (darkMode) => {
    setRenderScreenshotContent(darkMode ? Theme.Dark : Theme.Light);
  };

  const downloadScreenshot = (title: string, onComplete: () => void) => {
    html2canvas(document.getElementById(SHAREABLE_SCREENSHOT_ID), {
      onclone: function cloneDocument(clonedDoc) {
        clonedDoc.getElementById(SHAREABLE_SCREENSHOT_ID).style.display = 'block';
      },
      scale: 2,
    }).then((canvas) => {
      canvas.toBlob((blob) => {
        saveAs(blob, kebabCase(title.substring(0, 20)));
      });
      onComplete();
    });
  };

  useLayoutEffect(() => {
    if (renderScreenshotContent) {
      downloadScreenshot(title, () => setRenderScreenshotContent(false));
    }
  }, [renderScreenshotContent, title]);

  return { onDownloadScreenshot, renderScreenshotContent };
};

const getContainerWidthClass = (length: number) => {
  if (length > 1500) {
    return styles.xxl;
  } else if (length > 1250) {
    return styles.xl;
  } else if (length > 1000) {
    return styles.lg;
  } else if (length > 750) {
    return styles.md;
  }
  return '';
};

export default function ForumPostScreenshot({
  title,
  body,
  renderScreenshotContent,
  length,
}: {
  title: string;
  body: string;
  renderScreenshotContent: RenderScreenshotContent;
  length: number;
}) {
  const darkMode = renderScreenshotContent === Theme.Dark;

  return (
    <div
      id={SHAREABLE_SCREENSHOT_ID}
      className={clsx(styles.forumPostScreenshotContainer, getContainerWidthClass(length), {
        [styles.formPostScreenshotContainerDark]: darkMode,
      })}
    >
      <h1
        className={`my-0 font-suisse-works text-headline-sm sm:text-headline-lg ${
          darkMode ? 'text-white' : 'text-black'
        }`}
      >
        The Information <span className="text-primary-magenta">Forum</span>
      </h1>
      <div className="font-suisse-neue text-subheadline-md">{title}</div>
      <div className="mt-24 text-body-lg">{body}</div>
    </div>
  );
}
